import { Group, Select, SelectProps, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { PrescriptionType, PrescriptionPackage } from 'models/prescription';
import { buildEmailBody, calculateDurationInDays } from 'utils/prescription';
import { useGroupCredits } from 'hooks/api/prescription/useGroupCredits';
import { formatPrice, LanguageCode } from 'utils/i18n';
import { useCreatePrescriptionFormContext } from 'context/FormContext';
import { usePatient, useUserData } from 'hooks/api/useUser';
import { PatientIdProps } from 'models/general';
import { showUsaFeature } from 'utils/app';
import { usePackages } from 'hooks/api/prescription/usePackages';

export const SelectPrescriptionPackage = ({ patientId }: PatientIdProps) => {
  const { t } = useTranslation();
  const {
    getInputProps,
    setFieldValue,
    values: { packageId, prescriptionType, groupId, language },
  } = useCreatePrescriptionFormContext();
  const { data: groupCredits, isLoading } = useGroupCredits(groupId);
  const { data: packages } = usePackages();
  const { data: patientData } = usePatient({ patientId });
  const { data: userData } = useUserData();

  const changeEmail = (newPackageId: string, newLanguage: string) => {
    const prescriptionPackage = packages?.find((packageData) => packageData.id === newPackageId);
    setFieldValue('emailBody', buildEmailBody({ prescriptionPackage, patientData, language: newLanguage, userData }));
    setFieldValue('emailSubject', t('prescription_mail_subject', { lng: newLanguage }));
  };

  const renderAvailablePrescriptionsLabel = (availablePrescriptions?: number) => {
    return availablePrescriptions && availablePrescriptions > 0
      ? t('prescriptions_left_label', { availablePrescriptions })
      : t('no_prescription_left_label');
  };

  const renderInfoLabel = (item: PrescriptionPackage) => {
    return prescriptionType === PrescriptionType.Free
      ? renderAvailablePrescriptionsLabel(groupCredits?.[item.id])
      : formatPrice(item.price);
  };

  const renderDurationLabel = (item: PrescriptionPackage) => {
    const durationInDays = calculateDurationInDays(item.duration);
    return `${durationInDays} ${t(durationInDays === 1 ? 'day_label' : 'days_label')} `;
  };

  const renderSelectOption: SelectProps['renderOption'] = ({ option }) => {
    const pkg = packages?.find((p) => p.id === option.value);
    return (
      <Group justify={'space-between'} w={'100%'}>
        <Text>{option.label}</Text>
        <Text>{pkg && renderInfoLabel(pkg)}</Text>
      </Group>
    );
  };

  return (
    <>
      <Select
        label={t('add_prescript_package_label')}
        placeholder={t('add_prescript_package_placeholder')}
        data={packages?.map((item) => ({
          label: renderDurationLabel(item),
          value: item.id,
          disabled:
            !groupId ||
            !prescriptionType ||
            (prescriptionType === PrescriptionType.Free && (!groupCredits?.[item.id] || groupCredits?.[item.id] <= 0)),
        }))}
        renderOption={renderSelectOption}
        {...getInputProps('packageId')}
        value={packageId || null}
        onChange={(value) => {
          if (!value) return;
          setFieldValue('packageId', value);
          changeEmail(value, language);
        }}
        data-testid={'input-prescription-package'}
        disabled={isLoading}
      />
      {prescriptionType === PrescriptionType.Paid && (
        <Text size={'xs'} ta={'right'} data-testid={'label-prescription-prices'}>
          {t('add_prescript_package_prices')}
        </Text>
      )}
      {prescriptionType === PrescriptionType.Free && (
        <Text size={'xs'} data-testid={'label-prescription-no-credits'}>
          {t('add_prescript_package_no_credits')}
        </Text>
      )}
      {!showUsaFeature && (
        <Select
          label={t('add_prescript_language_label')}
          labelProps={{ 'data-testid': 'input-label-prescription-language' }}
          defaultValue={!showUsaFeature && language}
          data={Object.values(LanguageCode)
            .filter((lang) => lang !== LanguageCode.American_English)
            .map((lang) => ({
              value: lang,
              label: t(`lang_${lang}`),
            }))}
          {...getInputProps('language')}
          onChange={(value) => {
            if (!value) return;
            setFieldValue('language', value);
            changeEmail(packageId, value);
          }}
          data-testid={'input-language'}
        />
      )}
    </>
  );
};
